import React, { memo, useMemo, useState, useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import "./styles.scss";

const AttemptDialog = ({
  open = false,
  setOpen = () => {},
  practice = {},
  onAttempt = () => {},
  onViewAttempts = () => {},
  hideViewAttempts = false,
}) => {
  const [timeStatus, setTimeStatus] = useState({
    message: "",
    isAccessible: true,
  });
  const [, setCountdown] = useState("");

  // Check if test can be attempted based on time constraints and attempt status
  const canAttemptTest = useMemo(() => {
    // If no time constraints, allow unlimited attempts
    if (!practice?.startDateTime && !practice?.endDateTime) {
      return true;
    }

    // If test has time constraints and is already attempted, prevent re-attempt
    if (practice?.attempted) {
      return false;
    }

    // Otherwise, check time window accessibility
    return timeStatus.isAccessible;
  }, [
    practice?.startDateTime,
    practice?.endDateTime,
    practice?.attempted,
    timeStatus.isAccessible,
  ]);

  useEffect(() => {
    if (!practice?.startDateTime && !practice?.endDateTime) {
      setTimeStatus({
        message: "This test can be attempted multiple times",
        isAccessible: true,
        type: "unlimited",
      });
      return;
    }

    const updateStatus = () => {
      const now = new Date();
      const startDate = practice?.startDateTime
        ? new Date(practice.startDateTime)
        : null;
      const endDate = practice?.endDateTime
        ? new Date(practice.endDateTime)
        : null;

      // Test hasn't started yet
      if (startDate && now < startDate) {
        const diff = startDate - now;
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        const countdown = `${minutes}m ${seconds}s`;
        setCountdown(countdown);
        setTimeStatus({
          message: `Test will start in ${countdown}`,
          isAccessible: false,
          type: "upcoming",
        });
        return;
      }

      // Test has ended
      if (endDate && now > endDate) {
        setCountdown("");
        setTimeStatus({
          message: practice?.attempted
            ? "You have already attempted this test"
            : "Test has ended",
          isAccessible: false,
          type: "ended",
        });
        return;
      }

      // Test is ongoing but already attempted
      if (practice?.attempted) {
        setTimeStatus({
          message:
            "You have already attempted this test. This test can only be attempted once.",
          isAccessible: false,
          type: "attempted",
        });
        return;
      }

      // Test is ongoing and not attempted
      if (endDate && now <= endDate) {
        const diff = endDate - now;
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        const countdown = `${minutes}m ${seconds}s`;
        setCountdown(countdown);
        setTimeStatus({
          message: `Test will end in ${countdown}. This test can only be attempted once.`,
          isAccessible: true,
          type: "ongoing",
        });
        return;
      }
    };

    // Update immediately and then every second
    updateStatus();
    const interval = setInterval(updateStatus, 1000);

    return () => clearInterval(interval);
  }, [practice?.startDateTime, practice?.endDateTime, practice?.attempted]);

  const getStatusColor = () => {
    switch (timeStatus.type) {
      case "upcoming":
        return { bg: "#FFF3E0", text: "#FF6636" };
      case "ongoing":
      case "unlimited":
        return { bg: "#E8F5E9", text: "#4CAF50" };
      case "ended":
      case "attempted":
        return { bg: "#FFEBEE", text: "#F44336" };
      default:
        return { bg: "#E8F5E9", text: "#4CAF50" };
    }
  };

  const isWriting = practice?.type === "writing";

  const getButtonText = () => {
    if (isWriting) {
      return "Start Writing";
    }
    // For tests without time constraints
    if (!practice?.startDateTime && !practice?.endDateTime) {
      return "Attempt Quiz";
    }

    // For tests with time constraints
    switch (timeStatus.type) {
      case "upcoming":
        return "Not Started Yet";
      case "ended":
        return "Test Ended";
      case "attempted":
        return "Already Attempted";
      default:
        return "Attempt Quiz";
    }
  };

  return (
    <Modal show={open} onHide={() => setOpen(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title
          style={{ fontSize: "16px", lineHeight: "22px", fontWeight: 500 }}
        >
          {practice?.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-5">
        <div style={{ textAlignLast: "center" }}>
          <div className="px-3">
          <div
            style={{
              fontSize: "16px",
              fontWeight: "500",
              lineHeight: "22px",
              textAlign: "left",
              color: "#1D2026",
              marginBottom: "20px",
            }}
          >
            {isWriting ? "Type:" : "No. of Questions:"}{" "}
            <span
              style={{
                color: "#FF6636",
                fontSize: "14px",
                fontWeight: "400",
                marginLeft: "25px",
              }}
            >
              {isWriting ? "Writing Task" : practice?.questions?.length}
            </span>
          </div>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "500",
              lineHeight: "22px",
              textAlign: "left",
              color: "#1D2026",
              marginBottom: "20px",
            }}
          >
            Total Time:{" "}
            <span
              style={{
                color: "#FF6636",
                fontSize: "14px",
                fontWeight: "400",
                marginLeft: "25px",
              }}
            >
              {practice?.duration
                ? `${practice?.duration} minutes`
                : "Unlimited"}
            </span>
            </div>
            {practice?.startDateTime && (
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  lineHeight: "22px",
                  textAlign: "left",
                  color: "#1D2026",
                  marginBottom: "20px",
                }}
              >
                Available From:{" "}
                <span
                  style={{
                    color: "#FF6636",
                    fontSize: "14px",
                    fontWeight: "400",
                    marginLeft: "25px",
                  }}
                >
                  {new Date(practice.startDateTime).toLocaleString()}
                </span>
              </div>
            )}
            {practice?.endDateTime && (
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  lineHeight: "22px",
                  textAlign: "left",
                  color: "#1D2026",
                  marginBottom: "20px",
                }}
              >
                Available Until:{" "}
                <span
                  style={{
                    color: "#FF6636",
                    fontSize: "14px",
                    fontWeight: "400",
                    marginLeft: "25px",
                  }}
                >
                  {new Date(practice.endDateTime).toLocaleString()}
                </span>
              </div>
            )}
            {timeStatus.message && (
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  lineHeight: "20px",
                  textAlign: "center",
                  color: getStatusColor().text,
                  marginBottom: "20px",
                  padding: "10px",
                  backgroundColor: getStatusColor().bg,
                  borderRadius: "4px",
                }}
              >
                {timeStatus.message}
              </div>
            )}
          </div>
        </div>
        {(isWriting || practice?.questions?.length > 0) ? (
          <div
            className="d-flex justify-content-center mt-3"
            style={{ gap: "15px" }}
          >
            {!hideViewAttempts && (
              <Button
                variant="light"
                onClick={onViewAttempts}
                style={{ height: "48px" }}
                className="start-classroom-styles px-3"
              >
                View Attempts
              </Button>
            )}
            <Button
              onClick={onAttempt}
              variant="primary"
              className="attempt-handler-button-styles m-0 px-3"
              disabled={!canAttemptTest}
              style={
                !canAttemptTest ? { opacity: 0.6, cursor: "not-allowed" } : {}
              }
            >
              {getButtonText()}
            </Button>
          </div>
        ) : (
          ""
        )}
      </Modal.Body>
    </Modal>
  );
};

export default memo(AttemptDialog);
